import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import Button from 'components/Button/Button';
import IconButton from 'components/IconButton/IconButton';
import TextField from 'components/TextField/TextField';
import Tooltip from 'components/Tooltip/Tooltip';
import {
  ANALYSIS_TITLE_FIELD_NAME, MAX_ANALYSIS_TITLE_LENGTH,
} from 'constants/general';
import { BUTTON_VARIANT, ICON_BUTTON_SIZES, TOOLTIP_POSITIONS } from 'constants/interfaces';

import type IAnalysisSidebarHeader from './interfaces/IAnalysisSidebarHeader';

import styles from './scss/AnalysisSidebarHeader.module.scss';

function AnalysisSidebarHeader({
  title,
  analysis,
  isUpdating,
  open,
  onBack,
  onRenameAnalysis,
}: IAnalysisSidebarHeader): React.JSX.Element {
  const { t } = useTranslation();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title);
  const [titleError, setTitleError] = useState('');
  const isSavingTitleRef = useRef(false);

  useEffect(() => {
    setDraftTitle(title);
    setTitleError('');
    setIsSavingTitle(false);
    isSavingTitleRef.current = false;

    if (!open) {
      setIsEditingTitle(false);
    }
  }, [open, title]);

  const focusTitleField = () => {
    window.setTimeout(() => {
      document.getElementById(ANALYSIS_TITLE_FIELD_NAME)?.focus();
    }, 0);
  };

  const validateDraftTitle = () => {
    if (draftTitle.trim()) {
      setTitleError('');
      return true;
    }

    setTitleError(t('errors.nameRequired'));
    focusTitleField();
    return false;
  };

  const handleSaveTitle = async () => {
    if (isSavingTitleRef.current) return;
    if (!analysis) return;
    if (!validateDraftTitle()) return;

    const nextTitle = draftTitle.trim();

    if (nextTitle === title) {
      setDraftTitle(nextTitle);
      setIsEditingTitle(false);
      return;
    }

    try {
      isSavingTitleRef.current = true;
      setIsSavingTitle(true);

      const updatedAnalysis = await onRenameAnalysis(
        analysis.valueChainMapId,
        analysis.id,
        nextTitle,
      );

      setDraftTitle(updatedAnalysis.name);
      setIsEditingTitle(false);
    } catch {
      setTitleError(t('common.unknownError'));
      focusTitleField();
    } finally {
      isSavingTitleRef.current = false;
      setIsSavingTitle(false);
    }
  };

  const handleBackButton = () => {
    if (isEditingTitle && !draftTitle.trim()) {
      validateDraftTitle();
      return;
    }

    onBack();
  };

  const handleEditTitle = () => {
    if (!analysis) return;

    setIsEditingTitle(true);
    setTitleError('');
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDraftTitle(event.target.value);

    if (event.target.value.trim()) {
      setTitleError('');
    }
  };

  const handleTitleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    await handleSaveTitle();
  };

  const handleTitleBlur = async () => {
    await handleSaveTitle();
  };

  return (
    <div className={styles.header}>
      <IconButton
        icon={<ArrowLeftIcon aria-hidden="true" />}
        onClick={handleBackButton}
        className={styles.backButton}
        size={ICON_BUTTON_SIZES.small}
        ariaLabel={t('common.back')}
        labelKey="common.back"
      />
      {isEditingTitle ? (
        <TextField
          name={ANALYSIS_TITLE_FIELD_NAME}
          value={draftTitle}
          onChange={handleTitleChange}
          onKeyDown={handleTitleKeyDown}
          onBlur={handleTitleBlur}
          maxLength={MAX_ANALYSIS_TITLE_LENGTH}
          autoFocus
          disabled={isUpdating || isSavingTitle}
          error={titleError}
          className={styles.headerTitleField}
        />
      ) : (
        <Tooltip
          hint={t('valueChainMap.clickToRename')}
          className={styles.headerTitleTooltip}
          hintClassName={styles.headerTitleTooltipHint}
          position={TOOLTIP_POSITIONS.BOTTOM}
        >
          <Button
            type="button"
            variant={BUTTON_VARIANT.ghost}
            className={styles.headerTitleButton}
            onClick={handleEditTitle}
            aria-label={t('valueChainMap.clickToRename')}
            labelKey="valueChainMap.clickToRename"
            title={title}
          />
        </Tooltip>
      )}
    </div>
  );
}

export default AnalysisSidebarHeader;
